import React, { useState, useMemo, useEffect, useRef } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import { ArrowBigLeft, Search } from "lucide-react"; // Lucide search icon
import Button from "../components/Button/Button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

const InvoiceUpload = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [view, setView] = useState("list");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [fromCalendarOpen, setFromCalendarOpen] = useState(false);
  const [toCalendarOpen, setToCalendarOpen] = useState(false);

  const calendarRef = useRef(null);
  const inputRef = useRef(null); // For From Date input
  const toInputRef = useRef(null); // For To Date input

  const [formData, setFormData] = useState({
    distributorName: "",
    arnCode: "",
    invoiceNumber: "",
    fromDate: "",
    toDate: "",
    amount: "",
    gst: "",
    remark: "",
  });

  // Columns
  const columns = useMemo(
    () => [
      { Header: "Distributor Name", accessor: "distributorName", size: 200 },
      { Header: "APRN Code", accessor: "arnCode", size: 120 },
      { Header: "Invoice Number", accessor: "invoiceNumber", size: 300 },
      { Header: "Invoice Month", accessor: "invoiceMonth", size: 200 },
      { Header: "Amount", accessor: "amount", size: 100 },
      { Header: "GST", accessor: "gst", size: 80 },
      { Header: "Total Amount", accessor: "totalAmount", size: 120 },
      { Header: "Status", accessor: "status", size: 100 },
      { Header: "UTR No", accessor: "utrNo", size: 150 },
      { Header: "Remark", accessor: "remark", size: 200 },
      { Header: "Rejection Remark", accessor: "rejectionRemark", size: 200 },
      {
        Header: "Invoice",
        accessor: "invoiceFile",
        size: 120,
        Cell: ({ value }) => (
          <a
            href={value}
            download
            className="text-blue-600 underline hover:text-blue-800"
          >
            Download
          </a>
        ),
      },
      {
        Header: "Calculation File",
        accessor: "calculationFile",
        size: 150,
        Cell: ({ value }) => (
          <a
            href={value}
            download
            className="text-blue-600 underline hover:text-blue-800"
          >
            Download
          </a>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    const handler = (e) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target) &&
        toInputRef.current &&
        !toInputRef.current.contains(e.target)
      ) {
        setFromCalendarOpen(false);
        setToCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Data
  const fullData = useMemo(
    () => [
      {
        distributorName: "ABC Distributors",
        arnCode: "ARN123456",
        invoiceNumber: "INV0001",
        invoiceMonth: "July 2025",
        amount: "₹50,000",
        gst: "₹9,000",
        totalAmount: "₹59,000",
        status: "Approved",
        utrNo: "UTR987654",
        remark: "Paid",
        rejectionRemark: "-",
        invoiceFile: "/downloads/invoice1.pdf",
        calculationFile: "/downloads/calculation1.xlsx",
      },
      {
        distributorName: "XYZ Traders",
        arnCode: "ARN654321",
        invoiceNumber: "INV0002",
        invoiceMonth: "July 2025",
        amount: "₹30,000",
        gst: "₹5,400",
        totalAmount: "₹35,400",
        status: "Rejected",
        utrNo: "",
        remark: "",
        rejectionRemark: "Missing UTR",
        invoiceFile: "/downloads/invoice2.pdf",
        calculationFile: "/downloads/calculation2.xlsx",
      },
    ],
    []
  );

  // Filtered data
  const filteredData = useMemo(() => {
    return fullData.filter((row) => {
      const matchesSearch =
        row.distributorName.toLowerCase().includes(search.toLowerCase()) ||
        row.arnCode.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter
        ? row.status.toLowerCase() === statusFilter.toLowerCase()
        : true;
      return matchesSearch && matchesStatus;
    });
  }, [search, statusFilter, fullData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData({
      distributorName: "",
      arnCode: "",
      invoiceNumber: "",
      fromDate: "",
      toDate: "",
      amount: "",
      gst: "",
      remark: "",
    });
    setFromDate(null);
    setToDate(null);
  };

  return (
    <div className="max-w-screen-xl mx-auto p-4 space-y-4">
      {/* Toggle Button */}
      <div className="flex justify-start items-center">
        {view === "list" ? (
          <Button variant="default" onClick={() => setView("add")}>
            Add Invoice
          </Button>
        ) : (
          <Button variant="outline" onClick={() => setView("list")}>
            <div className="flex items-center gap-2">
              <ArrowBigLeft className="w-5 h-5" />
              Back to List
            </div>
          </Button>
        )}
      </div>

      {/* Conditionally Render Views */}
      {view === "list" ? (
        <div className="bg-white p-4 rounded shadow space-y-4 mt-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 className="text-xl font-semibold underline">
              Distributor List
            </h2>

            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
              {/* Search Input */}
              <div className="relative w-full md:w-64">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="w-4 h-4 text-gray-400" />
                </span>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by Distributor or APRN"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Status Dropdown */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Status</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>

          <ReusableTable columns={columns} data={filteredData} />
        </div>
      ) : (
        <div className="bg-white p-4 rounded shadow mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Distributor Name */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Distributor Name
              </label>
              <InputText
                name="distributorName"
                value={formData.distributorName}
                onChange={handleChange}
                className="w-full px-2"
                placeholder="Enter Distributor Name"
              />
            </div>

            {/* ARN Code */}
            <div>
              <label className="block text-sm font-medium mb-1">APRN Code</label>
              <InputText
                name="arnCode"
                value={formData.arnCode}
                onChange={handleChange}
                className="w-full px-2"
                placeholder="Enter APRN Code"
              />
            </div>

            {/* Invoice Number */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Invoice Number
              </label>
              <InputText
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={handleChange}
                className="w-full px-2"
                placeholder="Enter Invoice Number"
              />
            </div>

            {/* Invoice From Date */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1">
                Invoice From Date
              </label>
              <input
                type="text"
                readOnly
                value={fromDate ? fromDate.toLocaleDateString() : ""}
                onClick={() => setFromCalendarOpen(!fromCalendarOpen)}
                placeholder="Select From Date"
                className="w-full border border-gray-300 rounded-md px-3 py-2 cursor-pointer"
                ref={inputRef}
              />
              {fromCalendarOpen && (
                <div
                  className="absolute z-10 bg-white shadow-md mt-2 transform scale-75 origin-top-left"
                  ref={calendarRef}
                >
                  <Calendar
                    onChange={(date) => {
                      setFromDate(date);
                      setFormData((prev) => ({
                        ...prev,
                        fromDate: date.toISOString().slice(0, 10),
                      }));
                      setFromCalendarOpen(false);
                    }}
                    value={fromDate}
                  />
                </div>
              )}
            </div>

            {/* To Date with react-calendar */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1">
                Invoice To Date
              </label>
              <input
                type="text"
                readOnly
                value={toDate ? toDate.toLocaleDateString() : ""}
                onClick={() => setToCalendarOpen(!toCalendarOpen)}
                placeholder="Select To Date"
                className="w-full border border-gray-300 rounded-md px-3 py-2 cursor-pointer"
                ref={toInputRef}
              />
              {toCalendarOpen && (
                <div
                  className="absolute z-10 bg-white shadow-md mt-2 transform scale-75 origin-top-left"
                  ref={calendarRef}
                >
                  <Calendar
                    onChange={(date) => {
                      setToDate(date);
                      setFormData((prev) => ({
                        ...prev,
                        toDate: date.toISOString().slice(0, 10),
                      }));
                      setToCalendarOpen(false);
                    }}
                    value={toDate}
                  />
                </div>
              )}
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium mb-1">Amount</label>
              <InputText
                name="amount"
                type="text"
                value={formData.amount}
                onChange={handleChange}
                className="w-full px-2"
                placeholder="Enter Amount"
              />
            </div>

            {/* GST */}
            <div>
              <label className="block text-sm font-medium mb-1">GST</label>
              <InputText
                name="gst"
                type="text"
                value={formData.gst}
                onChange={handleChange}
                className="w-full px-2"
                placeholder="Enter GST"
              />
            </div>

            {/* Remark (span 3 columns) */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium mb-1">Remark</label>
              <InputTextarea
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                className="w-full px-2 py-1"
                rows={3}
                placeholder="Enter Remark"
              />
            </div>

            {/* Invoice Upload File */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Invoice Upload File
              </label>
              <input
                type="file"
                name="invoiceFile"
                className="w-full text-sm file:py-2 file:px-4 file:bg-purple-100 file:text-purple-700 file:rounded file:border-0 hover:file:bg-purple-200"
              />
            </div>

            {/* Calculation File Upload */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Calculation File Upload
              </label>
              <input
                type="file"
                name="calculationFile"
                className="w-full text-sm file:py-2 file:px-4 file:bg-purple-100 file:text-purple-700 file:rounded file:border-0 hover:file:bg-purple-200"
              />
            </div>
            <div></div>
          </div>
          <div className="flex gap-4 justify-end items-center mt-4">
            <Button type="reset" variant="default">
              Reset
            </Button>
            <Button type="submit" variant="outline" onClick={handleReset}>
              Submit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceUpload;
